import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { coerceCheckboxValue } from 'utils/checkbox-value';

export type CheckboxProps = {
  value?: unknown;
  onChange?: (value: boolean) => void;
  debounce?: number;
  disabled?: boolean;
  htmlId?: string;
  autoFocus?: boolean;
};

export default function Checkbox({
  value: initialValue,
  onChange,
  debounce,
  disabled,
  htmlId,
  autoFocus
}: CheckboxProps) {
  const [value, setValue] = useState(coerceCheckboxValue(initialValue));
  const isUserInteractionRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const ref = useRef<HTMLInputElement>(null);
  onChangeRef.current = onChange;

  useEffect(() => {
    isUserInteractionRef.current = false;
    setValue(coerceCheckboxValue(initialValue));
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!isUserInteractionRef.current) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      onChangeRef.current?.(value);
    }, debounce);
    return () => {
      clearTimeout(timeout);
    };
  }, [value, debounce]);

  const classes = clsx(
    'h-5 w-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-600 bg-white',
    disabled ? 'opacity-[70%] cursor-not-allowed' : 'cursor-pointer'
  );

  return (
    <input
      ref={ref}
      id={htmlId}
      type='checkbox'
      className={classes}
      checked={value}
      onChange={(e) => {
        isUserInteractionRef.current = true;
        setValue(e.target.checked);
      }}
      disabled={disabled}
    />
  );
}
